import React, { useCallback, useMemo, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

import NavBar from "../../components/common/NavBar";

const ADVERTISING_DASHBOARD_ANALYTICS = gql`
    query AdvertisingDashboardAnalytics(
        $startDate: Date
        $endDate: Date
        $sponsorId: ID
        $campaignId: ID
        $status: String
        $screen: String
        $country: String
    ) {
        advertisingDashboardAnalytics(
            startDate: $startDate
            endDate: $endDate
            sponsorId: $sponsorId
            campaignId: $campaignId
            status: $status
            screen: $screen
            country: $country
        ) {
            summary {
                impressions
                clicks
                ctr
                cpm
                cpc
                campaigns
                activeCampaigns
                budgetTotal
                spentTotal
                paidTotal
                remainingBudget
            }
            sponsorOptions {
                id
                label
            }
            campaignOptions {
                id
                label
            }
            statusOptions
            daily {
                date
                impressions
                clicks
                ctr
            }
            campaigns {
                campaignId
                campaignName
                sponsorId
                sponsorName
                status
                impressions
                clicks
                ctr
                cpm
                cpc
                budgetTotal
                spentTotal
            }
            creatives {
                creativeId
                creativeTitle
                creativeType
                campaignName
                impressions
                clicks
                ctr
            }
            screens {
                label
                impressions
                clicks
                ctr
            }
            zones {
                label
                impressions
                clicks
                ctr
            }
            countries {
                label
                impressions
                clicks
                ctr
            }
        }
    }
`;

const AD_FILTER_INPUTS = gql`
    query AdFilterInputs {
        adZones {
            id
            key
            name
        }
        adZoneRates {
            id
            country
        }
    }
`;

function getCountryLabel(code, t) {
    const labels = {
        US: t("United States"),
        CA: t("Canada"),
    };
    return labels[code] || code;
}

function toInputDate(date) {
    return date.toISOString().slice(0, 10);
}

function formatNumber(value) {
    return new Intl.NumberFormat("en-US").format(value || 0);
}

function formatCurrency(value) {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
    }).format(value || 0);
}

function formatStatus(value, t) {
    if (!value) return t ? t("All Statuses") : "All Statuses";
    const formatted = value
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    return t ? t(formatted) : formatted;
}

function sortOptions(options) {
    return [...options].sort((first, second) =>
        String(first.label).localeCompare(String(second.label), undefined, {
            sensitivity: "base",
            numeric: true,
        })
    );
}

function SummaryCard({ icon, label, value, hint }) {
    return (
        <div className="col-12 col-sm-6 col-xl-3">
            <div className="small-box bg-white" style={{ borderRadius: 16, boxShadow: "0 8px 24px rgba(0,0,0,0.08)" }}>
                <div className="inner">
                    <h3 style={{ color: "#111827" }}>{value}</h3>
                    <p style={{ color: "#4b5563", fontWeight: 700, marginBottom: 4 }}>{label}</p>
                    <span className="text-muted text-sm">{hint}</span>
                </div>
                <div className="icon" style={{ color: "rgba(0,0,0,0.16)" }}>
                    <i className={icon} />
                </div>
            </div>
        </div>
    );
}

function ChartCard({ title, children, empty, t }) {
    return (
        <div className="card" style={{ borderRadius: 16, border: "1px solid rgba(0,0,0,0.08)" }}>
            <div className="card-header border-0">
                <h3 className="card-title" style={{ fontWeight: 800 }}>{title}</h3>
            </div>
            <div className="card-body">
                {empty ? (
                    <div className="text-center text-muted py-5">{t ? t("noAdvertisingDataForPeriod") : "No advertising data for this period."}</div>
                ) : (
                    <div style={{ width: "100%", height: 300 }}>{children}</div>
                )}
            </div>
        </div>
    );
}

function DateFilter({ id, label, value, onChange }) {
    return (
        <div className="advertising-date-filter">
            <label htmlFor={id}>{label}</label>
            <div className="advertising-date-input-wrap">
                <i className="far fa-calendar-alt" />
                <input
                    id={id}
                    type="date"
                    value={value}
                    onChange={(event) => onChange(event.target.value)}
                />
            </div>
        </div>
    );
}

function SelectFilter({ id, label, icon, value, onChange, options, emptyLabel }) {
    return (
        <div className="advertising-filter-field">
            <label htmlFor={id}>{label}</label>
            <div className="advertising-select-wrap">
                <i className={icon} />
                <select
                    id={id}
                    value={value}
                    onChange={(event) => onChange(event.target.value)}
                >
                    <option value="">{emptyLabel}</option>
                    {options.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
}

function CampaignMetricsTable({ campaigns, t }) {
    if (!campaigns.length) {
        return null;
    }

    return (
        <div className="card" style={{ borderRadius: 16, border: "1px solid rgba(0,0,0,0.08)" }}>
            <div className="card-header border-0">
                <h3 className="card-title" style={{ fontWeight: 800 }}>{t("Campaign Details")}</h3>
            </div>
            <div className="card-body table-responsive p-0">
                <table className="table table-hover mb-0">
                    <thead>
                    <tr>
                        <th>{t("Campaign")}</th>
                        <th>{t("Sponsor")}</th>
                        <th>{t("Status")}</th>
                        <th className="text-right">{t("Impressions")}</th>
                        <th className="text-right">{t("Clicks")}</th>
                        <th className="text-right">{t("CTR")}</th>
                        <th className="text-right">{t("CPM")}</th>
                        <th className="text-right">{t("CPC")}</th>
                        <th className="text-right">{t("Budget")}</th>
                        <th className="text-right">{t("Spent")}</th>
                    </tr>
                    </thead>
                    <tbody>
                    {campaigns.map((campaign) => (
                        <tr key={campaign.campaignId}>
                            <td className="font-weight-bold">{campaign.campaignName || "—"}</td>
                            <td>{campaign.sponsorName || "—"}</td>
                            <td>
                                <span className="badge badge-light" style={{ textTransform: "capitalize" }}>
                                    {formatStatus(campaign.status, t)}
                                </span>
                            </td>
                            <td className="text-right">{formatNumber(campaign.impressions)}</td>
                            <td className="text-right">{formatNumber(campaign.clicks)}</td>
                            <td className="text-right">{campaign.ctr || 0}%</td>
                            <td className="text-right">{formatCurrency(campaign.cpm)}</td>
                            <td className="text-right">{formatCurrency(campaign.cpc)}</td>
                            <td className="text-right">{formatCurrency(campaign.budgetTotal)}</td>
                            <td className="text-right">{formatCurrency(campaign.spentTotal)}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function AdvertisingDashboard() {
    const { t: originalT } = useTranslation();
    const t = useCallback((key) => originalT(`advertisingDashboard.${key}`), [originalT]);
    const today = useMemo(() => new Date(), []);
    const defaultStartDate = useMemo(() => {
        const date = new Date(today);
        date.setDate(date.getDate() - 29);
        return date;
    }, [today]);

    const [startDate, setStartDate] = useState(toInputDate(defaultStartDate));
    const [endDate, setEndDate] = useState(toInputDate(today));
    const [sponsorId, setSponsorId] = useState("");
    const [campaignId, setCampaignId] = useState("");
    const [status, setStatus] = useState("");
    const [screen, setScreen] = useState("");
    const [country, setCountry] = useState("");

    const navBarElem = {
        pageTitle: t("Advertising"),
        links: [
            { pageTitle: t("Advertising"), navLink: null, active: false },
            { pageTitle: t("Dashboard"), navLink: `/advertise/dashboard`, active: true },
        ],
    };

    const { data, loading, error } = useQuery(ADVERTISING_DASHBOARD_ANALYTICS, {
        variables: {
            startDate,
            endDate,
            sponsorId: sponsorId || null,
            campaignId: campaignId || null,
            status: status || null,
            screen: screen || null,
            country: country || null,
        },
        fetchPolicy: "cache-and-network",
    });
    const { data: filterData } = useQuery(AD_FILTER_INPUTS, {
        fetchPolicy: "cache-first",
    });

    const analytics = data?.advertisingDashboardAnalytics;
    const summary = analytics?.summary || {};
    const sponsorOptions = sortOptions((analytics?.sponsorOptions || []).map((option) => ({
        value: option.id,
        label: option.label,
    })));
    const campaignOptions = sortOptions((analytics?.campaignOptions || []).map((option) => ({
        value: option.id,
        label: option.label,
    })));
    const statusOptions = sortOptions((analytics?.statusOptions || []).map((option) => ({
        value: option,
        label: formatStatus(option, t),
    })));
    const screenOptions = sortOptions((filterData?.adZones || []).map((zone) => ({
        value: zone.key,
        label: zone.name || zone.key,
    })));
    const countryOptions = useMemo(() => {
        const codes = new Set();
        (filterData?.adZoneRates || []).forEach((rate) => {
            const code = String(rate?.country || "").toUpperCase();
            if (code) codes.add(code);
        });

        if (!codes.size) return [
            { value: "US", label: t("United States") },
            { value: "CA", label: t("Canada") },
        ];

        return Array.from(codes)
            .map((code) => ({
                value: code,
                label: getCountryLabel(code, t),
            }))
            .sort((first, second) =>
                first.label.localeCompare(second.label, undefined, {
                    sensitivity: "base",
                    numeric: true,
                })
            );
    }, [filterData, t]);
    const daily = analytics?.daily || [];
    const campaigns = analytics?.campaigns || [];
    const creatives = analytics?.creatives || [];
    const zones = analytics?.zones || [];
    const countries = analytics?.countries || [];

    const resetFilters = () => {
        setSponsorId("");
        setCampaignId("");
        setStatus("");
        setScreen("");
        setCountry("");
        setStartDate(toInputDate(defaultStartDate));
        setEndDate(toInputDate(today));
    };

    return (
        <div className="content-wrapper">
            <NavBar navBarElem={navBarElem} />

            <section className="content">
                <div className="container-fluid">
                    <div className="card border-0 shadow-sm" style={{ borderRadius: 16 }}>
                        <div className="card-body">
                            <div className="row align-items-end">
                                <div className="col-12 col-md-6">
                                    <h4 className="mb-1" style={{ fontWeight: 800 }}>{t("Dashboard")}</h4>
                                    <p className="text-muted mb-md-0">
                                        {t("performanceOverview")}
                                    </p>
                                </div>

                                <div className="col-12 col-md-6 mt-4 mt-md-0">
                                    <div className="advertising-filter-panel advertising-date-panel">
                                        <DateFilter
                                            id="advertising-dashboard-start-date"
                                            label={t("Start Date")}
                                            value={startDate}
                                            onChange={setStartDate}
                                        />

                                        <div className="advertising-date-separator">
                                            <i className="fas fa-arrow-right" />
                                        </div>

                                        <DateFilter
                                            id="advertising-dashboard-end-date"
                                            label={t("End Date")}
                                            value={endDate}
                                            onChange={setEndDate}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="advertising-filter-panel advertising-select-panel mt-3">
                                <SelectFilter
                                    id="advertising-dashboard-sponsor"
                                    label={t("Sponsor")}
                                    icon="fas fa-user-shield"
                                    value={sponsorId}
                                    onChange={(value) => {
                                        setSponsorId(value);
                                        setCampaignId("");
                                    }}
                                    options={sponsorOptions}
                                    emptyLabel={t("All Sponsors")}
                                />

                                <SelectFilter
                                    id="advertising-dashboard-campaign"
                                    label={t("Campaign")}
                                    icon="fas fa-bullseye"
                                    value={campaignId}
                                    onChange={setCampaignId}
                                    options={campaignOptions}
                                    emptyLabel={t("All Campaigns")}
                                />

                                <SelectFilter
                                    id="advertising-dashboard-status"
                                    label={t("Status")}
                                    icon="fas fa-signal"
                                    value={status}
                                    onChange={(value) => {
                                        setStatus(value);
                                        setCampaignId("");
                                    }}
                                    options={statusOptions}
                                    emptyLabel={t("All Statuses")}
                                />

                                <SelectFilter
                                    id="advertising-dashboard-screen"
                                    label={t("Screen")}
                                    icon="fas fa-window-maximize"
                                    value={screen}
                                    onChange={setScreen}
                                    options={screenOptions}
                                    emptyLabel={t("All Screens")}
                                />

                                <SelectFilter
                                    id="advertising-dashboard-country"
                                    label={t("Country")}
                                    icon="fas fa-globe-americas"
                                    value={country}
                                    onChange={setCountry}
                                    options={countryOptions}
                                    emptyLabel={t("All Countries")}
                                />

                                <button
                                    type="button"
                                    className="advertising-reset-button"
                                    onClick={resetFilters}
                                >
                                    <i className="fas fa-undo-alt" />
                                    {t("Reset")}
                                </button>
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div className="alert alert-danger">
                            {getLocalizedGraphQLError(error, t, "errors.somethingWentWrong")}
                        </div>
                    )}

                    {loading && !analytics ? (
                        <div className="text-center text-muted py-5">{t("Loading advertising analytics...")}</div>
                    ) : (
                        <>
                            <div className="row">
                                <SummaryCard
                                    icon="fas fa-eye"
                                    label={t("Impressions")}
                                    value={formatNumber(summary.impressions)}
                                    hint={t("Total ad views")}
                                />
                                <SummaryCard
                                    icon="fas fa-mouse-pointer"
                                    label={t("Clicks")}
                                    value={formatNumber(summary.clicks)}
                                    hint={t("Total ad clicks")}
                                />
                                <SummaryCard
                                    icon="fas fa-percentage"
                                    label={t("CTR")}
                                    value={`${summary.ctr || 0}%`}
                                    hint={t("Click-through rate")}
                                />
                                <SummaryCard
                                    icon="fas fa-chart-line"
                                    label={t("CPM")}
                                    value={formatCurrency(summary.cpm)}
                                    hint={t("Cost per thousand impressions")}
                                />
                                <SummaryCard
                                    icon="fas fa-hand-pointer"
                                    label={t("CPC")}
                                    value={formatCurrency(summary.cpc)}
                                    hint={t("Cost per click")}
                                />
                                <SummaryCard
                                    icon="fas fa-bullseye"
                                    label={t("Campaigns")}
                                    value={formatNumber(summary.campaigns)}
                                    hint={`${summary.activeCampaigns || 0} ${t("active")}`}
                                />
                                <SummaryCard
                                    icon="fas fa-wallet"
                                    label={t("Paid")}
                                    value={formatCurrency(summary.paidTotal)}
                                    hint={t("Confirmed ad orders")}
                                />
                                <SummaryCard
                                    icon="fas fa-coins"
                                    label={t("Budget")}
                                    value={formatCurrency(summary.budgetTotal)}
                                    hint={`${formatCurrency(summary.remainingBudget)} ${t("remaining")}`}
                                />
                                <SummaryCard
                                    icon="fas fa-receipt"
                                    label={t("Spent")}
                                    value={formatCurrency(summary.spentTotal)}
                                    hint={t("Tracked campaign spend")}
                                />
                            </div>

                            <div className="row">
                                <div className="col-12 col-xl-8">
                                    <ChartCard title={t("Daily Performance")} empty={!daily.length} t={t}>
                                        <ResponsiveContainer>
                                            <LineChart data={daily} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="date" />
                                                <YAxis allowDecimals={false} />
                                                <Tooltip />
                                                <Line
                                                    type="monotone"
                                                    dataKey="impressions"
                                                    name={t("Impressions")}
                                                    stroke="#2563eb"
                                                    strokeWidth={3}
                                                    dot={false}
                                                />
                                                <Line
                                                    type="monotone"
                                                    dataKey="clicks"
                                                    name={t("Clicks")}
                                                    stroke="#16a34a"
                                                    strokeWidth={3}
                                                    dot={false}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </ChartCard>
                                </div>

                                <div className="col-12 col-xl-4">
                                    <ChartCard title={t("Traffic By Country")} empty={!countries.length} t={t}>
                                        <ResponsiveContainer>
                                            <BarChart data={countries} layout="vertical" margin={{ top: 10, right: 20, left: 35, bottom: 0 }}>
                                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                                <XAxis type="number" allowDecimals={false} />
                                                <YAxis dataKey="label" type="category" width={95} />
                                                <Tooltip />
                                                <Bar dataKey="impressions" name={t("Impressions")} fill="#2563eb" radius={[0, 8, 8, 0]} />
                                                <Bar dataKey="clicks" name={t("Clicks")} fill="#16a34a" radius={[0, 8, 8, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </ChartCard>
                                </div>

                                <div className="col-12 col-xl-6">
                                    <ChartCard title={t("Traffic By Ad Zone")} empty={!zones.length} t={t}>
                                        <ResponsiveContainer>
                                            <BarChart data={zones} layout="vertical" margin={{ top: 10, right: 20, left: 35, bottom: 0 }}>
                                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                                <XAxis type="number" allowDecimals={false} />
                                                <YAxis dataKey="label" type="category" width={95} />
                                                <Tooltip />
                                                <Bar dataKey="impressions" name={t("Impressions")} fill="#2563eb" radius={[0, 8, 8, 0]} />
                                                <Bar dataKey="clicks" name={t("Clicks")} fill="#16a34a" radius={[0, 8, 8, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </ChartCard>
                                </div>

                                <div className="col-12 col-xl-6">
                                    <ChartCard title={t("Creative Performance")} empty={!creatives.length} t={t}>
                                        <ResponsiveContainer>
                                            <BarChart data={creatives} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="creativeTitle" />
                                                <YAxis allowDecimals={false} />
                                                <Tooltip />
                                                <Bar dataKey="impressions" name={t("Impressions")} fill="#7c3aed" radius={[8, 8, 0, 0]} />
                                                <Bar dataKey="clicks" name={t("Clicks")} fill="#16a34a" radius={[8, 8, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </ChartCard>
                                </div>

                                <div className="col-12">
                                    <ChartCard title={t("Campaign Performance")} empty={!campaigns.length} t={t}>
                                        <ResponsiveContainer>
                                            <BarChart data={campaigns} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="campaignName" />
                                                <YAxis allowDecimals={false} />
                                                <Tooltip />
                                                <Bar dataKey="impressions" name={t("Impressions")} fill="#2563eb" radius={[8, 8, 0, 0]} />
                                                <Bar dataKey="clicks" name={t("Clicks")} fill="#16a34a" radius={[8, 8, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </ChartCard>
                                </div>

                                <div className="col-12">
                                    <CampaignMetricsTable campaigns={campaigns} t={t} />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </section>

            <style>{`
                .advertising-filter-panel {
                    padding: 12px;
                    border-radius: 18px;
                    background: linear-gradient(135deg, rgba(37,99,235,0.08), rgba(22,163,74,0.07));
                    border: 1px solid rgba(37,99,235,0.12);
                }

                .advertising-date-panel {
                    display: grid;
                    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
                    align-items: end;
                    gap: 12px;
                }

                .advertising-select-panel {
                    display: grid;
                    grid-template-columns: repeat(5, minmax(0, 1fr)) auto;
                    align-items: end;
                    gap: 12px;
                }

                .advertising-date-filter label,
                .advertising-filter-field label {
                    display: block;
                    margin: 0 0 6px;
                    color: #4b5563;
                    font-size: 0.72rem;
                    font-weight: 900;
                    letter-spacing: 0.05em;
                    text-transform: uppercase;
                }

                .advertising-date-input-wrap,
                .advertising-select-wrap {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    min-height: 46px;
                    padding: 0 12px;
                    border-radius: 14px;
                    background: #fff;
                    border: 1px solid rgba(17,24,39,0.12);
                    box-shadow: 0 8px 18px rgba(17,24,39,0.06);
                    transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
                }

                .advertising-date-input-wrap:focus-within,
                .advertising-select-wrap:focus-within {
                    border-color: #2563eb;
                    box-shadow: 0 0 0 4px rgba(37,99,235,0.12), 0 10px 22px rgba(17,24,39,0.08);
                    transform: translateY(-1px);
                }

                .advertising-date-input-wrap i,
                .advertising-select-wrap i {
                    color: #2563eb;
                    font-size: 0.95rem;
                    flex: 0 0 auto;
                }

                .advertising-date-input-wrap input,
                .advertising-select-wrap select {
                    width: 100%;
                    min-width: 0;
                    border: 0;
                    outline: 0;
                    background: transparent;
                    color: #111827;
                    font-size: 0.95rem;
                    font-weight: 800;
                    line-height: 1;
                    padding: 10px 0;
                }

                .advertising-select-wrap select {
                    cursor: pointer;
                    appearance: none;
                }

                .advertising-date-input-wrap input::-webkit-calendar-picker-indicator {
                    cursor: pointer;
                    opacity: 0.68;
                    filter: invert(34%) sepia(94%) saturate(1696%) hue-rotate(211deg) brightness(95%) contrast(96%);
                }

                .advertising-date-separator {
                    width: 34px;
                    height: 46px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    color: #2563eb;
                    font-size: 0.85rem;
                    opacity: 0.78;
                }

                .advertising-reset-button {
                    min-height: 46px;
                    padding: 0 16px;
                    border: 0;
                    border-radius: 14px;
                    background: #111827;
                    color: #fff;
                    font-weight: 900;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    box-shadow: 0 10px 22px rgba(17,24,39,0.16);
                    transition: transform 0.15s ease, box-shadow 0.15s ease;
                }

                .advertising-reset-button:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 12px 26px rgba(17,24,39,0.2);
                }

                .advertising-reset-button:focus {
                    outline: 0;
                    box-shadow: 0 0 0 4px rgba(17,24,39,0.16), 0 12px 26px rgba(17,24,39,0.2);
                }

                @media (max-width: 1199.98px) {
                    .advertising-select-panel {
                        grid-template-columns: repeat(3, minmax(0, 1fr));
                    }
                }

                @media (max-width: 767.98px) {
                    .advertising-select-panel {
                        grid-template-columns: 1fr;
                    }
                }

                @media (max-width: 575.98px) {
                    .advertising-date-panel {
                        grid-template-columns: 1fr;
                    }

                    .advertising-date-separator {
                        display: none;
                    }
                }
            `}</style>
        </div>
    );
}

export default AdvertisingDashboard;
